
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { PurchaseOrderModel } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

interface Row {
  purchaseOrderItemId: number;
  productName: string;
  sku: string;
  variant?: string;
  orderedQuantity: number;
  receivedQuantity: number;
  unitCost: number;
  newOrderedQuantity: number;
  error: string;
}

@Component
export default class AdjustQuantityDialog extends Vue {
  @Prop({ required: true }) public readonly purchaseOrder!: PurchaseOrderModel;
  @Prop({ default: false }) public readonly open!: boolean;

  public rows: Row[] = [];
  public reason = '';
  public notes = '';
  public searchQuery = '';
  public saving = false;
  public attemptedSubmit = false;

  @Watch('open')
  public onOpenChange(next: boolean): void {
    if (next) {
      this.seedRows();
    }
  }

  public created(): void {
    if (this.open) {
      this.seedRows();
    }
  }

  public get changedRows(): Row[] {
    return this.rows.filter((r) => r.newOrderedQuantity !== r.orderedQuantity);
  }

  public get hasLineErrors(): boolean {
    return this.rows.some((r) => Boolean(r.error));
  }

  public get reasonMissing(): boolean {
    return !this.reason || this.reason.trim() === '';
  }

  public get canSubmit(): boolean {
    return this.changedRows.length > 0 && !this.hasLineErrors && !this.reasonMissing && !this.saving;
  }

  public get totalUnitChange(): number {
    return this.changedRows.reduce((s, r) => {
      const delta = r.newOrderedQuantity - r.orderedQuantity;
      return Number.isFinite(delta) ? s + Math.abs(delta) : s;
    }, 0);
  }

  public get totalValueChange(): number {
    return this.changedRows.reduce((s, r) => {
      const delta = r.newOrderedQuantity - r.orderedQuantity;
      return Number.isFinite(delta) ? s + delta * (Number(r.unitCost) || 0) : s;
    }, 0);
  }

  public get visibleRows(): Row[] {
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) {
      return this.rows;
    }
    return this.rows.filter(
      (r) => r.productName.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q),
    );
  }

  public seedRows(): void {
    const items: any[] = (this.purchaseOrder && (this.purchaseOrder as any).items) || [];
    // Sequelize BIGINT ids can surface as numeric strings in JSON; normalize
    // so downstream validation and Map keys behave uniformly.
    const rows: Row[] = [];
    for (const it of items) {
      const lineId = Number(it.id);
      if (!Number.isFinite(lineId) || lineId <= 0) {
        continue;
      }
      // Use landed cost when available (it includes allocated shipping/tax
      // and matches how the PO is valued), otherwise fall back to unit_price.
      // PurchaseOrderItem has no `unitCost` field — reading it zeroes out
      // the summary card and the detail dialog.
      const landedCost = Number(it.extra && it.extra.landed_cost);
      const fallbackPrice = Number(it.unitPrice);
      // Treat 0 as a valid landed cost — only fall back when missing/NaN.
      const unitCost = Number.isFinite(landedCost)
        ? landedCost
        : (Number.isFinite(fallbackPrice) ? fallbackPrice : 0);
      rows.push({
        purchaseOrderItemId: lineId,
        productName: it.productName || it.name || '(unnamed)',
        sku: it.sku || '',
        variant: it.variantTitle || it.variant || undefined,
        orderedQuantity: Number(it.orderedQuantity) || 0,
        receivedQuantity: Number(it.receivedQuantity) || 0,
        unitCost,
        newOrderedQuantity: Number(it.orderedQuantity) || 0,
        error: '',
      });
    }
    this.rows = rows;
    this.reason = '';
    this.notes = '';
    this.searchQuery = '';
    this.attemptedSubmit = false;
  }

  public validateRow(row: Row): void {
    if (!Number.isInteger(row.newOrderedQuantity) || row.newOrderedQuantity < 0) {
      row.error = 'Must be a non-negative integer';
    } else if (row.newOrderedQuantity < row.receivedQuantity) {
      row.error = `Cannot go below received quantity (${row.receivedQuantity})`;
    } else {
      row.error = '';
    }
  }

  public updateRow(lineId: number, newQty: number): void {
    const row = this.rows.find((r) => r.purchaseOrderItemId === lineId);
    if (!row) {
      return;
    }
    row.newOrderedQuantity = Number.isFinite(newQty) ? newQty : NaN;
    this.validateRow(row);
  }

  public deltaClass(row: Row): string {
    const d = row.newOrderedQuantity - row.orderedQuantity;
    if (d > 0) {
      return 'green--text';
    }
    if (d < 0) {
      return 'red--text';
    }
    return 'grey--text';
  }

  public formatDelta(row: Row): string {
    const d = row.newOrderedQuantity - row.orderedQuantity;
    if (!Number.isFinite(d) || d === 0) {
      return '0';
    }
    return d > 0 ? `+${d}` : `${d}`;
  }

  public get poCurrencyCode(): string {
    return (this.purchaseOrder && (this.purchaseOrder as any).currencyCode) || 'USD';
  }

  public formatMoney(v: number): string {
    const n = Number(v);
    if (!Number.isFinite(n)) {
      return CurrencyHelper.formatCurrencyWithCode(0, this.poCurrencyCode);
    }
    return CurrencyHelper.formatCurrencyWithCode(n, this.poCurrencyCode);
  }

  public formatSignedMoney(v: number): string {
    const n = Number(v);
    const sign = !Number.isFinite(n) || n >= 0 ? '+' : '-';
    const magnitude = !Number.isFinite(n) ? 0 : Math.abs(n);
    return `${sign}${CurrencyHelper.formatCurrencyWithCode(magnitude, this.poCurrencyCode)}`;
  }

  public onDialogInput(v: boolean): void {
    if (!v) {
      this.close();
    }
  }

  public close(): void {
    if (this.saving) {
      return;
    }
    this.$emit('close');
  }

  public async submit(): Promise<void> {
    this.attemptedSubmit = true;
    if (!this.canSubmit) {
      return;
    }

    this.saving = true;
    try {
      const payload = this.changedRows.map((r) => ({
        purchaseOrderItemId: r.purchaseOrderItemId,
        newOrderedQuantity: r.newOrderedQuantity,
      }));
      const trimmedNotes = (this.notes || '').trim();
      const result = await this.purchaseOrder.adjustQuantity(payload, this.reason.trim(), trimmedNotes || undefined);
      this.$emit('saved', result);
      // Emit directly — close() is guarded against `saving`, which is still
      // true here until the `finally` clears it. The guard is only meant to
      // block user-initiated Cancel/X, not the programmatic success dismiss.
      this.$emit('close');
    } catch (err: any) {
      const msg = (err && err.response && err.response.data && err.response.data.message)
        || (err && err.message)
        || 'Failed to save adjustment';
      this.$emit('error', msg);
    } finally {
      this.saving = false;
    }
  }
}
